import angular from 'angular'
import VendDetect from '@vend/detect'

import { findFirstFocusableChild } from './vd-focus.utility'

export default function vdFocus($rootScope, $timeout, $document) {
  'ngInject'
  /**
   * Attempts to focus the element directly or failing that, the next focusable child.
   *
   * @function focusNode
   *
   * @param {Node} element
   *        The element node to focus.
   *
   * @return {Node} The focused node.
   */
  function focusNode(element) {
    element.focus()

    if (element === $document[0].activeElement) {
      return element
    }

    const firstFocusableChild = findFirstFocusableChild({ element })

    if (firstFocusableChild) {
      firstFocusableChild.focus()
      return firstFocusableChild
    }
  }

  /**
   * Focus the element and select text.
   *
   * @function focusAndSelectText
   *
   * @param {Node} elementNode
   *        The element node to focus and select text
   */
  function focusAndSelectText(elementNode) {
    const focusedNode = focusNode(elementNode)

    if (focusedNode && focusedNode.setSelectionRange && focusedNode.value) {
      focusedNode.setSelectionRange(0, focusedNode.value.length)
    }
  }

  /**
   * Evaluates the focus options.
   *
   * @function evaluateFocusOptions
   *
   * @param {Node} elementNode
   *        The element node to focus.
   *
   * @param {Object} options
   *
   *        [options.isFocusable = true] {Boolean}
   *            Set the focus on an element when the given expression is evaluated to be true. If no
   *            expression is given, we assume the element requires the focus.
   *
   *        [options.selectText = false] {Boolean}
   *            If HTMLInputElement.setSelectionRange() is available, select all text within the element.
   *
   *        [options.focusDelay = 0] {Number}
   *            The delay before focusing the element. (Not available in IOS).
   *
   *        [options.focusOn] {String}
   *            The name of the $rootScope event that when emitted, will trigger the element to receive the focus.
   */
  function evaluateFocusOptions(elementNode, options) {
    const optionDelay = options.focusDelay || 0

    const shouldFocus =
      angular.isUndefined(options.isFocusable) || options.isFocusable

    if (!shouldFocus) {
      return
    }

    // Just focus
    if (!options.selectText) {
      // Do not use $timeout if IOS as it has to be the direct result of a user action for select / focus to work
      if (VendDetect.iOS) {
        focusNode(elementNode)
      } else {
        $timeout(() => focusNode(elementNode), optionDelay)
      }
      return
    }

    // Focus and Select Text
    if (VendDetect.iOS) {
      focusAndSelectText(elementNode)
    } else {
      $timeout(() => focusAndSelectText(elementNode), optionDelay)
    }
  }

  return {
    restrict: 'A',
    link(scope, $element, attrs) {
      const configOptions = scope.$eval(attrs.vdFocus) || {}
      const elementNode = $element[0]

      evaluateFocusOptions(elementNode, configOptions)

      // Bind listener
      if (configOptions.focusOn) {
        const focusListener = $rootScope.$on(
          configOptions.focusOn,
          ($event, eventOptions) => {
            const options = angular.merge({}, configOptions, eventOptions)
            evaluateFocusOptions(elementNode, options)
          }
        )

        // Unbind listener
        scope.$on('$destroy', focusListener)
      }
    },
  }
}
